import { RmaButtonBrand } from '@rma/generic/ui/button-brand';
import { tv } from 'tailwind-variants';

// Each list is the source of its type so the two cannot drift; the order is also the stories'
// row and column order.
export const RMA_BUTTON_APPEARANCES = ['solid', 'text', 'outline', 'icon', 'fab'] as const;
export type RmaButtonAppearance = (typeof RMA_BUTTON_APPEARANCES)[number];

export const RMA_BUTTON_TONES = ['brand', 'accent', 'danger', 'neutral'] as const;
type RmaButtonNamedTone = (typeof RMA_BUTTON_TONES)[number];

// The named tones are the ones the variant table below carries for every brand. The `string`
// arm keeps the input open: an unnamed tone renders the button's structure with no colours, and
// the caller supplies them as utility classes on the element — which win through the merge.
// `& {}` is what keeps autocomplete for the named tones.
export type RmaButtonTone = RmaButtonNamedTone | (string & {});
export type RmaButtonShape = 'default' | 'round';

// Icon-only appearances are already circles and need an accessible name, so shape and the dev-mode
// name check both key off this one test.
export function isIconOnlyAppearance(appearance: RmaButtonAppearance): boolean {
  return appearance === 'icon' || appearance === 'fab';
}

const NAMED_TONES: readonly string[] = RMA_BUTTON_TONES;

/*
 * Every colour below is a literal, never a palette utility like `bg-blue-10`. The two brands
 * share palette *names* with different values, so a ramp class would resolve against whichever
 * theme the consuming app compiled — a dashboard-branded button rendered inside a public app
 * would come out as a hybrid of dashboard rung choices and public paint. Literals make a cell
 * mean the same thing everywhere, which is what lets `brand` be a per-usage input at all.
 *
 * They cannot be hoisted into named constants either: Tailwind compiles a utility only for a
 * class name it can read verbatim in the source, so `bg-[${RAMP.blue10}]` emits no CSS at all.
 * The trade is that the button does not follow later edits to a brand's ramp. Each literal is
 * the rung it was taken from:
 *
 *   dashboard  blue-10 #e5f0ff · blue-20 #b7d5ff · blue-60 #0067ff
 *              purple-10 #f0ecff · purple-20 #d0c1ff · purple-50 #683cee · purple-80 #2c0d88
 *              pink-10 #ffedee · pink-20 #ffc5c9 · pink-60 #e43542 · pink-70 #c9212d
 *              steel-10 #f9fafb · steel-20 #e1e4e7 · steel-40 #b3b8be · steel-90 #4c5258
 *   public     blue-50 #124faa · activity-50 #c204e9 · warning-50 #cc0000
 *   microsites ink #1c2522 · ink-muted #655f57 · sand #ece5d9 · hairline #e7dfd1
 *              status-negative #7c3a1d · on-accent #fff · radius-pill 99px
 *              (named tokens, because the microsites theme has no ramps)
 *
 * Microsites `accent` paints from the `--ms-accent` custom property the page root sets at
 * runtime. The ink fallback in `var(--ms-accent,#1c2522)` is required, because the page removes
 * the property when the profile's brand colour fails its contrast check. The theme names no disabled
 * token, so disabled ink is ink-muted and disabled fill is sand.
 *
 * The remaining values belong to no ramp: public's disabled ink and fill are Material's own
 * `rgba(0,0,0,0.33)` and `rgba(0,0,0,0.1)`, its neutral ink is `rgba(0,0,0,0.87)`, and the
 * shared disabled border is `rgba(0,0,0,0.12)`.
 *
 * The state layer Material painted on its ripple element is composited as a flat
 * `background-image` gradient over the fill, so `background-color` still reports the base fill
 * and the layer is readable independently. Hover and active carry
 * `not-disabled:not-aria-disabled:` guards rather than relying on variant order, so a disabled
 * host never takes a layer or an opacity change whatever the stylesheet ends up ordering.
 *
 * `!` on ink, weight and underline is not stylistic: the global link rules
 * (`a:not(.mdc-button)` in rma-globals/_typography.scss) sit in no cascade layer, and an
 * unlayered declaration outranks every layered utility whatever its specificity. Ink also
 * yields to `.rma-loading-indicator`, whose own `color: transparent !important` hides the
 * label behind the spinner and has to win — hence the `[&:not(.rma-loading-indicator)]` guard
 * on every ink class.
 *
 * The elevation triple and the disabled clears use `[box-shadow:...]` arbitrary properties
 * rather than `shadow-*` utilities, whose ring-variable composition would leave four
 * `0 0 #0000` entries in the computed value.
 */
const rmaButtonVariants = tv({
  base: [
    'relative box-border inline-flex appearance-none items-center justify-center select-none',
    // The gap is `:where` so it adds no specificity and a call-site margin class overrides it; the
    // `.rma-btn+` adjacency limits it to a button after another rmaButton, `ms-` keeps RTL right.
    'align-middle [:where(.mat-mdc-dialog-actions_.rma-btn+&)]:ms-2',
    'h-9 min-h-0 px-4 text-center text-base tracking-[normal] whitespace-nowrap',
    'cursor-pointer disabled:cursor-default aria-disabled:cursor-default',
    'border-0 border-solid bg-transparent no-underline!',
    // The state-layer scaffold: a transparent gradient in the rest state, swapped for the
    // tone's layer on interaction. Always present, exactly as it was on Material's ripple —
    // `background-color` reports the fill alone in every state.
    'bg-[image:linear-gradient(transparent,transparent)]',
    // The 48px pointer target, permitted to overflow the visible box.
    'after:absolute after:top-1/2 after:left-1/2 after:h-12 after:w-full after:min-w-12 after:-translate-x-1/2 after:-translate-y-1/2',
    // Every brand rings in black. Public `brand` reaches 2.72:1 against its own fill rather
    // than 3:1 — recorded in the library README as a known shortfall, not silently downgraded.
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#000]',
  ],
  variants: {
    brand: {
      dashboard: [
        'min-w-16 rounded-[8px] leading-4 font-medium!',
        'disabled:[&:not(.rma-loading-indicator)]:text-[#b3b8be]! aria-disabled:[&:not(.rma-loading-indicator)]:text-[#b3b8be]!',
      ],
      public: [
        // Material's button inherits its line-height, which is the public body's 24px.
        'min-w-0 rounded-[4px] leading-6 font-bold!',
        'disabled:[&:not(.rma-loading-indicator)]:text-[rgba(0,0,0,0.33)]! aria-disabled:[&:not(.rma-loading-indicator)]:text-[rgba(0,0,0,0.33)]!',
      ],
      // The pill is the only microsites button shape, so it sits on the brand row.
      microsites: [
        'min-w-0 rounded-[99px] leading-[normal] font-semibold!',
        'disabled:[&:not(.rma-loading-indicator)]:text-[#655f57]! aria-disabled:[&:not(.rma-loading-indicator)]:text-[#655f57]!',
      ],
    },
    appearance: {
      // Mirrors Material's mini-fab so its call sites can migrate; fill and ink come from the
      // `solid` cells, and the lift is `focus:` rather than `focus-visible:` to match Material.
      fab: [
        'h-10 w-10 min-w-0 rounded-[50%] px-2 text-2xl font-normal!',
        '[box-shadow:0_3px_5px_-1px_rgba(0,0,0,0.2),0_6px_10px_0_rgba(0,0,0,0.14),0_1px_18px_0_rgba(0,0,0,0.12)]',
        'hover:not-disabled:not-aria-disabled:[box-shadow:0_5px_5px_-3px_rgba(0,0,0,0.2),0_8px_10px_1px_rgba(0,0,0,0.14),0_3px_14px_2px_rgba(0,0,0,0.12)]',
        'focus:not-disabled:not-aria-disabled:[box-shadow:0_5px_5px_-3px_rgba(0,0,0,0.2),0_8px_10px_1px_rgba(0,0,0,0.14),0_3px_14px_2px_rgba(0,0,0,0.12)]',
        'active:not-disabled:not-aria-disabled:[box-shadow:0_7px_8px_-4px_rgba(0,0,0,0.2),0_12px_17px_2px_rgba(0,0,0,0.14),0_5px_22px_4px_rgba(0,0,0,0.12)]',
        'disabled:[box-shadow:none] aria-disabled:[box-shadow:none]',
      ],
      solid: '',
      text: 'px-2',
      // 15px, not 16px: the 1px border makes up the difference, as it did in Material.
      outline: 'border bg-[#fff] px-[15px] disabled:border-[rgba(0,0,0,0.12)] aria-disabled:border-[rgba(0,0,0,0.12)]',
      // Tone-independent in every brand: it inks in black, keeps that ink when disabled, and
      // dims with opacity instead of taking a state layer.
      icon: [
        'h-10 w-10 min-w-0 rounded-[50%] px-2 text-2xl font-normal!',
        '[&:not(.rma-loading-indicator)]:text-[#000]!',
        'disabled:[&:not(.rma-loading-indicator)]:text-[#000]! aria-disabled:[&:not(.rma-loading-indicator)]:text-[#000]!',
      ],
    },
    tone: { brand: '', accent: '', danger: '', neutral: '' },
    shape: { default: '', round: '' },
    fullWidth: { true: 'w-full' },
    // Elevation is orthogonal to colour: any appearance/tone pair except `fab`, which owns its
    // elevations, takes Material's elevation-1 triple, identical in both brands.
    raised: {
      true: '[box-shadow:0_3px_1px_-2px_rgba(0,0,0,0.2),0_2px_2px_0_rgba(0,0,0,0.14),0_1px_5px_0_rgba(0,0,0,0.12)] disabled:[box-shadow:none] aria-disabled:[box-shadow:none]',
    },
  },
  compoundVariants: [
    /* ── public ──
       Every state layer is the ink at the ripple's own opacity, whatever the appearance and
       tone: `currentcolor` is the ink the cell has just set, and resolves where the layer is
       painted. Neutral's 0.87 ink carries through the mix to reproduce Material's 0.0348 and
       0.1044. */
    {
      brand: 'public',
      appearance: ['solid', 'text', 'outline'],
      class: [
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(color-mix(in_srgb,currentcolor_4%,transparent),color-mix(in_srgb,currentcolor_4%,transparent))]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(color-mix(in_srgb,currentcolor_12%,transparent),color-mix(in_srgb,currentcolor_12%,transparent))]',
      ],
    },
    // The hover/active dim: Material 22 never emits the `.mat-mdc-flat-button` selector the
    // public overrides targeted, so restoring the dim is a listed deviation, not the capture.
    // `fab` shares the solid fill, so every `solid` cell below also lists it.
    {
      brand: 'public',
      appearance: ['solid', 'fab'],
      class: [
        'hover:not-disabled:not-aria-disabled:opacity-80 active:not-disabled:not-aria-disabled:opacity-80',
        'disabled:bg-[rgba(0,0,0,0.1)] aria-disabled:bg-[rgba(0,0,0,0.1)]',
      ],
    },
    { brand: 'public', appearance: 'outline', class: 'disabled:bg-transparent aria-disabled:bg-transparent' },
    {
      brand: 'public',
      appearance: 'icon',
      class: 'hover:not-disabled:not-aria-disabled:opacity-70 active:not-disabled:not-aria-disabled:opacity-70',
    },
    { brand: 'public', appearance: ['solid', 'fab'], tone: 'brand', class: 'bg-[#124faa] [&:not(.rma-loading-indicator)]:text-[#fff]!' },
    { brand: 'public', appearance: ['solid', 'fab'], tone: 'accent', class: 'bg-[#c204e9] [&:not(.rma-loading-indicator)]:text-[#fff]!' },
    { brand: 'public', appearance: ['solid', 'fab'], tone: 'danger', class: 'bg-[#cc0000] [&:not(.rma-loading-indicator)]:text-[#fff]!' },
    // Neutral inverts the usual arrangement — the surface is white and the tone is the ink.
    {
      brand: 'public',
      appearance: ['solid', 'fab'],
      tone: 'neutral',
      class: 'bg-[#fff] [&:not(.rma-loading-indicator)]:text-[rgba(0,0,0,0.87)]!',
    },
    // `border-*` on `text` is inert (border-width stays 0) and lets text and outline share a
    // cell, since their ink agrees in every tone.
    {
      brand: 'public',
      appearance: ['text', 'outline'],
      tone: 'brand',
      class: 'border-[#124faa] [&:not(.rma-loading-indicator)]:text-[#124faa]!',
    },
    {
      brand: 'public',
      appearance: ['text', 'outline'],
      tone: 'accent',
      class: 'border-[#c204e9] [&:not(.rma-loading-indicator)]:text-[#c204e9]!',
    },
    {
      brand: 'public',
      appearance: ['text', 'outline'],
      tone: 'danger',
      class: 'border-[#cc0000] [&:not(.rma-loading-indicator)]:text-[#cc0000]!',
    },
    {
      brand: 'public',
      appearance: ['text', 'outline'],
      tone: 'neutral',
      class: 'border-[rgba(0,0,0,0.87)] [&:not(.rma-loading-indicator)]:text-[rgba(0,0,0,0.87)]!',
    },

    /* ── dashboard ──
       Solid layers are opaque rungs and washes are the tone's tint, so both live per cell
       rather than deriving from the ink. Brand and neutral fill from the tint with dark ink;
       accent and danger fill from the strong rung with white ink. Both patterns are as
       captured. */
    {
      brand: 'dashboard',
      appearance: ['solid', 'fab'],
      tone: 'brand',
      class: [
        'bg-[#e5f0ff] [&:not(.rma-loading-indicator)]:text-[#0067ff]!',
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#b7d5ff,#b7d5ff)]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#b7d5ff,#b7d5ff)]',
      ],
    },
    {
      brand: 'dashboard',
      appearance: ['solid', 'fab'],
      tone: 'accent',
      class: [
        'bg-[#683cee] [&:not(.rma-loading-indicator)]:text-[#fff]!',
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#2c0d88,#2c0d88)]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#2c0d88,#2c0d88)]',
      ],
    },
    {
      brand: 'dashboard',
      appearance: ['solid', 'fab'],
      tone: 'danger',
      class: [
        'bg-[#e43542] [&:not(.rma-loading-indicator)]:text-[#fff]!',
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#c9212d,#c9212d)]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#c9212d,#c9212d)]',
      ],
    },
    {
      brand: 'dashboard',
      appearance: ['solid', 'fab'],
      tone: 'neutral',
      class: [
        'bg-[#f9fafb] [&:not(.rma-loading-indicator)]:text-[#4c5258]!',
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#e1e4e7,#e1e4e7)]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#e1e4e7,#e1e4e7)]',
      ],
    },
    { brand: 'dashboard', appearance: ['solid', 'fab'], class: 'disabled:bg-[#f9fafb] aria-disabled:bg-[#f9fafb]' },
    // Accent ink is purple-50 where Material inked `text` at purple-60 — the one tone whose
    // text and outline inks disagreed, reconciled into a single ink. A listed deviation.
    {
      brand: 'dashboard',
      appearance: ['text', 'outline'],
      tone: 'brand',
      class: [
        'border-[#b7d5ff] [&:not(.rma-loading-indicator)]:text-[#0067ff]!',
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#e5f0ff,#e5f0ff)]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#e5f0ff,#e5f0ff)]',
      ],
    },
    {
      brand: 'dashboard',
      appearance: ['text', 'outline'],
      tone: 'accent',
      class: [
        'border-[#d0c1ff] [&:not(.rma-loading-indicator)]:text-[#683cee]!',
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#f0ecff,#f0ecff)]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#f0ecff,#f0ecff)]',
      ],
    },
    {
      brand: 'dashboard',
      appearance: ['text', 'outline'],
      tone: 'danger',
      class: [
        'border-[#ffc5c9] [&:not(.rma-loading-indicator)]:text-[#e43542]!',
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#ffedee,#ffedee)]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#ffedee,#ffedee)]',
      ],
    },
    {
      brand: 'dashboard',
      appearance: ['text', 'outline'],
      tone: 'neutral',
      class: [
        'border-[#e1e4e7] [&:not(.rma-loading-indicator)]:text-[#4c5258]!',
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#f9fafb,#f9fafb)]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(#f9fafb,#f9fafb)]',
      ],
    },

    /* ── microsites ──
       State layers work as they do on public. Solid gets no opacity dim, because a profile's brand
       accent has no known darker rung to dim toward. */
    {
      brand: 'microsites',
      appearance: ['solid', 'text', 'outline'],
      class: [
        'hover:not-disabled:not-aria-disabled:bg-[image:linear-gradient(color-mix(in_srgb,currentcolor_4%,transparent),color-mix(in_srgb,currentcolor_4%,transparent))]',
        'active:not-disabled:not-aria-disabled:bg-[image:linear-gradient(color-mix(in_srgb,currentcolor_12%,transparent),color-mix(in_srgb,currentcolor_12%,transparent))]',
      ],
    },
    { brand: 'microsites', appearance: ['solid', 'fab'], class: 'disabled:bg-[#ece5d9] aria-disabled:bg-[#ece5d9]' },
    { brand: 'microsites', appearance: 'outline', class: 'disabled:bg-transparent aria-disabled:bg-transparent' },
    {
      brand: 'microsites',
      appearance: 'icon',
      class: 'hover:not-disabled:not-aria-disabled:opacity-70 active:not-disabled:not-aria-disabled:opacity-70',
    },
    {
      brand: 'microsites',
      appearance: ['solid', 'fab'],
      tone: 'brand',
      class: 'bg-[#1c2522] [&:not(.rma-loading-indicator)]:text-[#fff]!',
    },
    {
      brand: 'microsites',
      appearance: ['solid', 'fab'],
      tone: 'accent',
      class: 'bg-[var(--ms-accent,#1c2522)] [&:not(.rma-loading-indicator)]:text-[#fff]!',
    },
    {
      brand: 'microsites',
      appearance: ['solid', 'fab'],
      tone: 'danger',
      class: 'bg-[#7c3a1d] [&:not(.rma-loading-indicator)]:text-[#fff]!',
    },
    {
      brand: 'microsites',
      appearance: ['solid', 'fab'],
      tone: 'neutral',
      class: 'bg-[#ece5d9] [&:not(.rma-loading-indicator)]:text-[#1c2522]!',
    },
    {
      brand: 'microsites',
      appearance: ['text', 'outline'],
      tone: 'brand',
      class: 'border-[#1c2522] [&:not(.rma-loading-indicator)]:text-[#1c2522]!',
    },
    {
      brand: 'microsites',
      appearance: ['text', 'outline'],
      tone: 'accent',
      class: 'border-[var(--ms-accent,#1c2522)] [&:not(.rma-loading-indicator)]:text-[var(--ms-accent,#1c2522)]!',
    },
    {
      brand: 'microsites',
      appearance: ['text', 'outline'],
      tone: 'danger',
      class: 'border-[#7c3a1d] [&:not(.rma-loading-indicator)]:text-[#7c3a1d]!',
    },
    // Neutral borders with the hairline rather than the ink so it reads as secondary beside the
    // ink-filled primary.
    {
      brand: 'microsites',
      appearance: ['text', 'outline'],
      tone: 'neutral',
      class: 'border-[#e7dfd1] [&:not(.rma-loading-indicator)]:text-[#1c2522]!',
    },

    // Restates each brand's line-height after `icon` and `fab` set their font size: the merge
    // treats a font-size class as superseding an earlier leading class, so the brand-level value
    // is dropped for these appearances and has to come later.
    { brand: 'dashboard', appearance: ['icon', 'fab'], class: 'leading-4' },
    { brand: 'public', appearance: ['icon', 'fab'], class: 'leading-6' },
    { brand: 'microsites', appearance: ['icon', 'fab'], class: 'leading-[normal]' },

    // Public's pill, from Material's `.mat-rounded`. The dashboard has no pill button and one
    // is deliberately not invented, so `shape="round"` composes no classes there; microsites
    // is a pill already, so it composes none there either.
    { brand: 'public', shape: 'round', class: 'rounded-[100px] px-5 font-normal!' },
  ],
});

export interface RmaButtonClassOptions {
  brand: RmaButtonBrand;
  appearance: RmaButtonAppearance;
  tone: RmaButtonTone;
  shape: RmaButtonShape;
  fullWidth: boolean;
  raised: boolean;
  /** Author classes from the host element; merged last, so they win every conflict. */
  class?: string;
}

/**
 * The full class string for one button: three `rma-btn*` marker classes, then the variant output,
 * conflict-resolved through tailwind-merge so later classes (cells, then author classes)
 * replace earlier ones rather than fighting them in the cascade. The markers are hooks for tests
 * and consumer selectors with no rule of their own, except `rma-btn`, which the dialog-gap
 * utility in `base` selects on and so cannot be renamed or dropped.
 *
 * `round` is withheld from `icon` and `fab` because they are already circles — the pill radius
 * would replace its 50% radius. A tone outside the named set composes no colour classes; the
 * caller supplies its colours as utilities on the element.
 */
export function rmaButtonClasses(options: RmaButtonClassOptions): string {
  // SAFETY(legacy): unreviewed, pre-dates this rule.
  const tone = NAMED_TONES.includes(options.tone) ? (options.tone as RmaButtonNamedTone) : undefined;

  const variantClasses = rmaButtonVariants({
    brand: options.brand,
    appearance: options.appearance,
    tone,
    shape: isIconOnlyAppearance(options.appearance) ? 'default' : options.shape,
    fullWidth: options.fullWidth,
    // `fab` owns its elevations; `raised` would replace its rest shadow with level 1.
    raised: options.appearance === 'fab' ? false : options.raised,
    class: options.class,
  });

  return ['rma-btn', `rma-btn-${options.appearance}`, `rma-btn-${options.tone}`, variantClasses].join(' ');
}
